import type { ProgramBrand } from '@/utils/programBrand';

/**
 * Researched POC brand identities (brand color + monogram) keyed by workflow display name.
 * The UI renders a colored initials tile — there are no logo image files.
 *
 * Colors and monograms were researched (with an independent verification pass) for a POC and are
 * NOT guaranteed accurate — review before any production use. This is a best-effort fallback:
 * `getProgramBrand` prefers an explicitly-assigned brand hex on the workflow, and matching here is
 * by normalized display name, so a name that drifts in an environment simply falls back to a
 * generic treatment. See src/utils/programBrand.ts for the resolution logic.
 *
 * Each entry is:
 *   name     - workflow display name (the lookup is by its normalized form)
 *   colorHex - primary brand color (#RRGGBB): initials tile, tints, borders
 *   textHex  - brand color adjusted for >=4.5:1 contrast on white: text on light backgrounds
 *   monogram - 1-3 letter monogram shown on the initials tile
 */
export const PROGRAM_BRANDS: ReadonlyArray<ProgramBrand> = [
  { name: 'Takeda', colorHex: '#E1242A', textHex: '#C71F24', monogram: 'T' },
  { name: 'Arcutis', colorHex: '#FFD200', textHex: '#8A6D00', monogram: 'A' },
  { name: 'Thea', colorHex: '#003DA5', textHex: '#003DA5', monogram: 'TH' },
  { name: 'Bonafide Health', colorHex: '#34055A', textHex: '#34055A', monogram: 'B' },
  { name: 'Aucta', colorHex: '#136233', textHex: '#136233', monogram: 'A' },
  { name: 'Channel Markers', colorHex: '#585BA8', textHex: '#585BA8', monogram: 'CM' },
  { name: 'ConTIPI', colorHex: '#9C46AF', textHex: '#400A82', monogram: 'C' },
  { name: 'AbbVie', colorHex: '#071D49', textHex: '#071D49', monogram: 'AV' },
  { name: 'Bausch Health', colorHex: '#03838C', textHex: '#03727A', monogram: 'BH' },
  { name: 'Sun Pharma', colorHex: '#F7941E', textHex: '#B45F00', monogram: 'SP' },
  { name: 'Merck', colorHex: '#007A73', textHex: '#007A73', monogram: 'M' },
  { name: 'UBC - Servier', colorHex: '#1959A8', textHex: '#1959A8', monogram: 'S' },
  { name: 'UBC - Kalvista', colorHex: '#5BA829', textHex: '#3F7A1C', monogram: 'K' },
  { name: 'Bausch & Lomb', colorHex: '#0097A0', textHex: '#03838C', monogram: 'BL' },
  { name: 'Blink Lexicon Connective', colorHex: '#0055DC', textHex: '#0055DC', monogram: 'LX' },
  { name: 'AstraZeneca', colorHex: '#8A0051', textHex: '#8A0051', monogram: 'AZ' },
  { name: 'Amgen', colorHex: '#0063C3', textHex: '#0063C3', monogram: 'A' },
  { name: 'Exeltis', colorHex: '#0046AD', textHex: '#0046AD', monogram: 'E' },
  { name: 'Galderma', colorHex: '#00A0DF', textHex: '#00709C', monogram: 'G' },
  { name: 'MTPA', colorHex: '#0033A0', textHex: '#0033A0', monogram: 'MT' },
  { name: 'QM', colorHex: '#0072CE', textHex: '#005BA6', monogram: 'QM' },
  { name: 'UBC - Veloxis', colorHex: '#00639A', textHex: '#00639A', monogram: 'V' },
  { name: 'Milestone', colorHex: '#6E2585', textHex: '#6E2585', monogram: 'M' },
  { name: 'Boehringer', colorHex: '#00E47C', textHex: '#08312A', monogram: 'BI' },
  { name: 'UCB - Hybrid [Briviact]', colorHex: '#0054A4', textHex: '#0054A4', monogram: 'UCB' },
  { name: 'UCB - PAP', colorHex: '#0054A4', textHex: '#0054A4', monogram: 'UCB' },
  { name: 'UCB - Cash', colorHex: '#0054A4', textHex: '#0054A4', monogram: 'UCB' },
  { name: 'UBC Nestle', colorHex: '#00B2A9', textHex: '#007A74', monogram: 'NHS' },
  { name: 'Glenmark', colorHex: '#ED1C24', textHex: '#C5141B', monogram: 'G' },
  { name: 'Mannkind', colorHex: '#BD1A8D', textHex: '#BD1A8D', monogram: 'MK' },
  { name: 'Organon', colorHex: '#E20177', textHex: '#C00065', monogram: 'O' },
  { name: 'Agile', colorHex: '#00A99D', textHex: '#007971', monogram: 'AT' },
  { name: 'Halozyme (Antares)', colorHex: '#00A651', textHex: '#00753A', monogram: 'H' },
];
